import { Fragment, useEffect, useState } from 'react';
import { getAsset, listAliases, listClips, type Alias, type Clip } from '../api';
import { useAsync } from '../lib/async';
import { FamilyFilter, NO_FAMILY, serverFamily } from '../lib/family';
import { InlinePlayer } from '../lib/media';
import { Pager, ScanNote, useCursorList } from '../lib/paging';
import { replace } from '../lib/route';
import {
  Empty,
  Facts,
  Id,
  Link,
  Pill,
  Result,
  Toolbar,
  money,
  when,
} from '../lib/ui';

/**
 * The asset pool: everything the system has generated, what it cost, and whether
 * the reuse tier can see it.
 *
 * Every row is watchable in place. The portal used to hand out a `window.open`
 * link, which is a link and not a review tool — it loses the row, opens a bare
 * video with none of the provenance beside it, and gets eaten by popup blockers.
 * Since there is **no video moderation anywhere in this system** (a deliberate
 * decision on evidence: 20% false positives on known-good clips), a human
 * looking at the output is the only control that exists, and it should cost one
 * click.
 *
 * Filtering happens server-side now, which is a correctness fix rather than a
 * speed one: the old list took one scan page and filtered `kind` afterwards, so
 * `?kind=clip` returned however many clips fell in the first 200 rows and called
 * that all of them.
 */

const KINDS = ['', 'clip', 'character-art'];
const SCOPES = ['', 'generic', 'named', 'identifying'];

export function Clips({
  initialKind,
  initialFamily,
}: {
  initialKind?: string;
  initialFamily?: string;
}) {
  const [kind, setKind] = useState(initialKind ?? '');
  const [reuse, setReuse] = useState('');
  const [family, setFamily] = useState(initialFamily ?? '');
  const [playing, setPlaying] = useState<string | null>(null);

  useEffect(() => {
    replace({
      name: 'clips',
      ...(kind ? { kind } : {}),
      ...(family ? { familyId: family } : {}),
    });
  }, [kind, family]);

  // `/admin/clips` is the one endpoint with a word for "no owning family":
  // ?family=none is the shared pool, which is 123 of 133 assets and the normal
  // case rather than an anomaly.
  const familyParam = serverFamily(family, { noneParam: 'none' });

  const list = useCursorList<Clip>(
    (cursor) =>
      listClips({
        ...(kind ? { kind } : {}),
        ...(reuse ? { reuse } : {}),
        ...(familyParam ? { family: familyParam } : {}),
        ...(cursor ? { next: cursor } : {}),
      }),
    [kind, reuse, familyParam ?? ''],
  );

  return (
    <>
      <Toolbar>
        <span className="group">
          <label htmlFor="clip-kind">Kind</label>
          <select id="clip-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            {KINDS.map((value) => (
              <option key={value || 'all'} value={value}>
                {value || 'everything'}
              </option>
            ))}
          </select>
        </span>
        <span className="group">
          <label htmlFor="clip-scope">Scope</label>
          <select id="clip-scope" value={reuse} onChange={(e) => setReuse(e.target.value)}>
            {SCOPES.map((value) => (
              <option key={value || 'all'} value={value}>
                {value || 'any scope'}
              </option>
            ))}
          </select>
        </span>
        <FamilyFilter value={family} onChange={setFamily} />
      </Toolbar>

      <Result of={list.result} what="assets" retry={list.reload}>
        {(page) => (
          <>
            <Toolbar>
              <Pager list={list} what="assets" />
            </Toolbar>
            {family === NO_FAMILY && (
              <p className="muted small">
                The shared pool — assets with no owning family. These are the ones
                the reuse waterfall can serve to any household, and the only ones
                the control plane will sign a link for.
              </p>
            )}
            {!page.rows.length ? (
              <Empty what="assets" filtered={Boolean(kind || reuse || family)} />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Canonical</th>
                    <th>Kind</th>
                    <th>Scope</th>
                    <th className="num">Cost</th>
                    <th>Searchable</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {page.rows.map((clip) => (
                    // Fragment with a key, not `<>`: the player is a second <tr>
                    // and a table row cannot contain one, so the pair has to be
                    // siblings rather than nested.
                    <Fragment key={clip.assetId}>
                      <tr>
                        <td>
                          <code className="small">{clip.canonical}</code>
                          {clip.commissionId && (
                            <div className="small">
                              <Link
                                to={{ name: 'commission', commissionId: clip.commissionId }}
                              >
                                from a commission ›
                              </Link>
                            </div>
                          )}
                        </td>
                        <td className="muted">{clip.kind}</td>
                        <td>
                          <Pill tone={clip.shared ? 'on' : 'warn'}>
                            {clip.reuse ?? (clip.shared ? 'shared' : 'family')}
                          </Pill>
                        </td>
                        <td className="num muted">{money(clip.costUsd)}</td>
                        <td>
                          {clip.embedded ? (
                            <Pill tone="on">yes</Pill>
                          ) : (
                            // No embedding means nearest-neighbour search can
                            // never find it, so it gets regenerated instead of
                            // reused — real money, once per miss.
                            <Pill tone="warn">no embedding</Pill>
                          )}
                        </td>
                        <td className="muted nowrap">{when(clip.createdAt)}</td>
                        <td className="nowrap">
                          <button
                            className="link"
                            onClick={() =>
                              setPlaying(playing === clip.assetId ? null : clip.assetId)
                            }
                          >
                            {playing === clip.assetId ? 'close' : 'watch'}
                          </button>
                          <Link to={{ name: 'clips', assetId: clip.assetId }}>details</Link>
                        </td>
                      </tr>
                      {playing === clip.assetId && (
                        <tr>
                          <td colSpan={7}>
                            <InlinePlayer
                              assetId={clip.assetId}
                              {...(clip.storageKey ? { storageKey: clip.storageKey } : {})}
                              familyScoped={Boolean(clip.familyId)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
            <ScanNote listing={page} />
          </>
        )}
      </Result>
    </>
  );
}

/**
 * One asset, with everything that explains where it came from.
 *
 * The alias join is the subtle part and the reason this screen is worth having.
 * `showling-aliases` maps a canonical phrase to the asset that serves it, and it
 * is the index the reuse waterfall actually reads. If this asset's canonical now
 * points at a DIFFERENT asset, this one has been superseded: still stored, still
 * paid for, and never served again. That is invisible everywhere else in the
 * system, including on the list above.
 */
export function Asset({ assetId }: { assetId: string }) {
  const { result, reload } = useAsync(() => getAsset(assetId), [assetId]);

  return (
    <>
      <Toolbar>
        <Link to={{ name: 'clips' }}>‹ the pool</Link>
        <span className="spacer" />
        <code className="small">{assetId}</code>
        <button onClick={reload}>Refresh</button>
      </Toolbar>

      <Result of={result} what="this asset" retry={reload}>
        {(asset) => (
          <>
            <section className="detail">
              <h2>
                {asset.kind ?? 'asset'}{' '}
                <Pill tone={asset.shared ? 'on' : 'warn'}>
                  {asset.reuse ?? (asset.shared ? 'shared' : 'family')}
                </Pill>
                {asset.stale && (
                  <>
                    {' '}
                    <Pill tone="warn" title="past freshUntil: the veto stops offering it for reuse, but it stays watchable">
                      stale for reuse
                    </Pill>
                  </>
                )}
              </h2>

              <div className="split">
                <div>
                  <Facts
                    rows={[
                      ['Canonical', <code className="small">{asset.canonical}</code>],
                      ['Cost', money(asset.costUsd)],
                      ['Duration', asset.durationSeconds ? `${asset.durationSeconds}s` : null],
                      ['Provider', [asset.provider, asset.model].filter(Boolean).join(' / ')],
                      ['Source', asset.source],
                      ['Style', asset.styleId],
                      ['Created', when(asset.createdAt)],
                      ['Fresh until', when(asset.freshUntil)],
                      [
                        'Searchable',
                        asset.embedded ? (
                          <Pill tone="on">embedded</Pill>
                        ) : (
                          <Pill tone="warn">no embedding — the waterfall cannot find it</Pill>
                        ),
                      ],
                      ['Tag signature', <code className="small">{asset.tagSignature}</code>],
                      // THE THING, NOT THE FIELD — with the field on hover, so
                      // neither reader loses. shared/assets.py still writes the
                      // key under the name `storageKey` and renaming a stored field
                      // is a migration nobody has a reason to run, so the field
                      // name is real and worth being able to find. What is NOT
                      // real is the service: this value is a path inside the
                      // Blob container, and an operator who reads "S3 key" goes
                      // looking for a bucket in an account that no longer
                      // exists. The label is what they act on; the tooltip is
                      // what they grep the API response for.
                      [
                        'Blob path',
                        <code className="small" title="the API returns this as `storageKey` — the field name outlived the cloud it was named after">
                          {asset.storageKey}
                        </code>,
                      ],
                      [
                        'Family',
                        asset.familyId ? (
                          <Link to={{ name: 'families', familyId: asset.familyId }}>
                            {asset.familyName || asset.familyId}
                          </Link>
                        ) : (
                          <span className="muted">none — shared pool</span>
                        ),
                      ],
                    ]}
                  />
                </div>
                <div>
                  <InlinePlayer
                    assetId={assetId}
                    {...(asset.storageKey ? { storageKey: asset.storageKey } : {})}
                    {...(asset.url ? { url: asset.url } : {})}
                    {...(asset.contentType ? { contentType: asset.contentType } : {})}
                    familyScoped={Boolean(asset.notPlayable) && Boolean(asset.familyId)}
                  />
                  {asset.notPlayable && !asset.familyId && (
                    <p className="warn small">{asset.notPlayable}</p>
                  )}
                </div>
              </div>

              {asset.tags && Object.keys(asset.tags).length > 0 && (
                <>
                  <h3>Tags</h3>
                  <pre className="raw">{JSON.stringify(asset.tags, null, 2)}</pre>
                </>
              )}
            </section>

            <section className="detail">
              <h2>Where it came from</h2>
              {asset.commission ? (
                <Facts
                  rows={[
                    [
                      'Commission',
                      <Link
                        to={{ name: 'commission', commissionId: asset.commission.commissionId }}
                      >
                        {asset.commission.request || asset.commission.commissionId}
                      </Link>,
                    ],
                    ['Shot', asset.shotId],
                    [
                      'For',
                      asset.commission.familyId ? (
                        <Link to={{ name: 'families', familyId: asset.commission.familyId }}>
                          {asset.commission.familyName || asset.commission.familyId}
                        </Link>
                      ) : null,
                    ],
                    ['Baked with', asset.bakedWith],
                  ]}
                />
              ) : (
                <>
                  <Empty what="provenance" />
                  <p className="muted small">
                    `commissionId` and `shotId` are written on 10 of 133 asset
                    rows, so an older asset legitimately carries no back-reference.
                    This is a missing back-link, not a missing origin.
                  </p>
                </>
              )}
            </section>

            <section className="detail">
              <h2>The reuse waterfall</h2>
              {!asset.alias ? (
                <>
                  <Empty what="alias" />
                  <p className="muted small">
                    Nothing in showling-aliases maps this asset's canonical phrase
                    to anything, so the waterfall will never reach this asset by
                    phrase — only by vector similarity, and only if it is embedded.
                  </p>
                </>
              ) : asset.alias.pointsHere === false ? (
                <div className="notice notice-warn">
                  <b>Superseded.</b>
                  <span className="muted small">
                    The canonical phrase this asset was filed under now serves a
                    different asset. This one is still stored and still cost money,
                    and it will never be served again.
                  </span>
                  <span className="small">
                    now serves <Id value={asset.alias.assetId} chars={20} />
                  </span>
                </div>
              ) : (
                <Facts
                  rows={[
                    ['Serves', <code className="small">{asset.alias.canonical}</code>],
                    [
                      'Re-keyed from',
                      asset.alias.rekeyedFrom ? (
                        <code className="small">{asset.alias.rekeyedFrom}</code>
                      ) : null,
                    ],
                    ['Filed', when(asset.alias.at)],
                  ]}
                />
              )}
              {asset.canonical && <FanIn canonical={asset.canonical} assetId={assetId} />}
            </section>
          </>
        )}
      </Result>
    </>
  );
}

/**
 * Other phrases that resolve near this one.
 *
 * 207 aliases against 133 assets, so the fan-in is real and has never been
 * viewable. A substring search rather than an exact match, because the interesting
 * question is "what else did the canonicalizer file under something like this",
 * and it costs a scan either way — `canonical` IS the partition key and there is
 * no index.
 */
function FanIn({ canonical, assetId }: { canonical: string; assetId: string }) {
  // The leading token of a canonical is a style prefix that every phrase shares,
  // so searching on the whole string finds only itself. The middle words are
  // what actually discriminate.
  const probe = canonical.split(' ').slice(1, 4).join(' ');
  const { result, reload } = useAsync(
    () => listAliases({ q: probe, limit: 25 }),
    [probe],
  );

  if (!probe) return null;

  return (
    <>
      <h3>Phrases that land nearby</h3>
      <p className="muted small">
        Aliases containing <code>{probe}</code>. Not the full fan-in — a substring
        probe over a table whose partition key is the phrase itself.
      </p>
      <Result of={result} what="aliases" retry={reload}>
        {(page) =>
          !page.rows.length ? (
            <Empty what="nearby phrases" filtered />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Canonical</th>
                  <th>Serves</th>
                  <th>Re-keyed from</th>
                  <th>Filed</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((alias: Alias) => (
                  <tr
                    key={alias.canonical}
                    className={alias.assetId === assetId ? 'flagged' : ''}
                  >
                    <td>
                      <code className="small">{alias.canonical}</code>
                    </td>
                    <td>
                      {alias.assetId === assetId ? (
                        <Pill tone="on">this asset</Pill>
                      ) : alias.assetId ? (
                        <Link to={{ name: 'clips', assetId: alias.assetId }}>
                          <Id value={alias.assetId} chars={14} />
                        </Link>
                      ) : (
                        // An alias with no assetId is a dangling reuse entry:
                        // the waterfall would resolve the phrase and find nothing.
                        <Pill tone="bad">points at nothing</Pill>
                      )}
                    </td>
                    <td className="muted small">{alias.rekeyedFrom}</td>
                    <td className="muted nowrap">{when(alias.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Result>
    </>
  );
}
